import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiGithub, FiLinkedin, FiMail, FiArrowDown, FiMapPin } from "react-icons/fi";
import { PROFILE } from "@/data/portfolio";
import { HERO } from "@/constants/testIds";
import MagneticButton from "@/components/molecules/MagneticButton";

function TypingRole({ roles, interval = 2400 }) {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const role = roles[idx];
    let timer;
    if (!deleting && text === role) {
      timer = setTimeout(() => setDeleting(true), interval);
    } else if (deleting && text === "") {
      setDeleting(false);
      setIdx((i) => (i + 1) % roles.length);
    } else {
      timer = setTimeout(
        () => {
          setText((t) =>
            deleting ? role.substring(0, t.length - 1) : role.substring(0, t.length + 1)
          );
        },
        deleting ? 35 : 70
      );
    }
    return () => clearTimeout(timer);
  }, [text, deleting, idx, roles, interval]);

  return (
    <span className="font-mono text-[color:var(--accent)]">
      {text}
      <span className="inline-block w-[2px] h-[1em] align-middle bg-[color:var(--accent)] ml-1 animate-pulse" />
    </span>
  );
}

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.2 } },
};
const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(10px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.8, ease: [0.2, 0.8, 0.2, 1] } },
};

export default function Hero() {
  return (
    <section
      id="home"
      data-testid={HERO.section}
      className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden"
    >
      {/* grid bg */}
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      {/* gradient blob */}
      <motion.div
        animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[color:var(--accent)] opacity-[0.07] blur-3xl"
      />

      <div className="container-x relative w-full">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center"
        >
          {/* Left: text content */}
          <div className="lg:col-span-8">
            <motion.div variants={fadeUp} className="flex items-center gap-3 mb-6">
              <span
                data-testid={HERO.availableBadge}
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[color:var(--border-strong)] bg-[color:var(--surface)] text-xs font-mono"
              >
                <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" />
                Available for senior frontend roles
              </span>
              <span className="hidden sm:inline-flex items-center gap-1 text-xs font-mono text-[color:var(--text-muted)]">
                <FiMapPin size={12} /> {PROFILE.location}
              </span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              data-testid={HERO.name}
              className="font-display text-[clamp(2.75rem,8vw,6.5rem)] leading-[0.95] tracking-tighter"
            >
              {PROFILE.firstName}
              <br />
              {PROFILE.lastName}
              <span className="text-[color:var(--accent)]">.</span>
            </motion.h1>

            <motion.div
              variants={fadeUp}
              data-testid={HERO.role}
              className="mt-6 text-lg md:text-xl text-[color:var(--text-muted)] font-body"
            >
              Currently building as a <TypingRole roles={PROFILE.roles} />
            </motion.div>

            <motion.p
              variants={fadeUp}
              className="mt-5 max-w-xl text-base text-[color:var(--text-muted)] font-body leading-relaxed"
            >
              5+ years architecting scalable, accessible web &amp; mobile experiences
              across healthcare, legal-tech, HRMS and real-estate — powered by React,
              TypeScript and AI-assisted development.
            </motion.p>

            <motion.div variants={fadeUp} className="mt-8 flex flex-wrap items-center gap-3">
              <MagneticButton
                as="a"
                href="#contact"
                data-testid={HERO.ctaPrimary}
                className="bg-[color:var(--accent)] text-white hover:opacity-90 px-6 py-3 text-sm font-medium"
                strength={10}
              >
                Get in touch
                <span aria-hidden>→</span>
              </MagneticButton>
              <MagneticButton
                as="a"
                href={PROFILE.resumeUrl}
                data-testid={HERO.ctaSecondary}
                className="border border-[color:var(--border-strong)] text-[color:var(--text)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] px-6 py-3 text-sm font-medium"
                strength={8}
              >
                Download CV
              </MagneticButton>

              <div className="flex items-center gap-1.5 ml-1">
                <a
                  data-testid={HERO.socialLink("github")}
                  href={PROFILE.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="GitHub"
                  data-cursor-label="GitHub"
                  className="w-10 h-10 rounded-full border border-[color:var(--border-strong)] flex items-center justify-center text-[color:var(--text-muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
                >
                  <FiGithub size={15} />
                </a>
                <a
                  data-testid={HERO.socialLink("linkedin")}
                  href={PROFILE.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  data-cursor-label="LinkedIn"
                  className="w-10 h-10 rounded-full border border-[color:var(--border-strong)] flex items-center justify-center text-[color:var(--text-muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
                >
                  <FiLinkedin size={15} />
                </a>
                <a
                  data-testid={HERO.socialLink("email")}
                  href={`mailto:${PROFILE.email}`}
                  aria-label="Email"
                  data-cursor-label="Email"
                  className="w-10 h-10 rounded-full border border-[color:var(--border-strong)] flex items-center justify-center text-[color:var(--text-muted)] hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition"
                >
                  <FiMail size={15} />
                </a>
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div
              variants={fadeUp}
              className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[color:var(--border)] rounded-xl overflow-hidden border border-[color:var(--border)]"
            >
              {PROFILE.stats.map((s, i) => (
                <div
                  key={i}
                  data-testid={HERO.statCard(i)}
                  className="bg-[color:var(--bg)] p-5"
                >
                  <div className="font-display text-2xl md:text-3xl text-[color:var(--text)]">
                    {s.value}
                  </div>
                  <div className="label-mono mt-2">{s.label}</div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: image */}
          <motion.div
            variants={fadeUp}
            className="lg:col-span-4 relative"
          >
            <div className="relative max-w-sm mx-auto lg:ml-auto">
              <div className="absolute -inset-4 rounded-3xl bg-[color:var(--accent)] opacity-[0.12] blur-3xl" />
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[color:var(--border-strong)] bg-[color:var(--surface)]">
                <img
                  src={PROFILE.heroImage}
                  alt="Suyash Tanwar"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--bg)] via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <span className="label-mono text-white drop-shadow">@suyashtanwar06</span>
                  <span className="px-2 py-1 rounded-full bg-[color:var(--accent)] text-white text-[10px] font-mono uppercase tracking-widest">
                    Open to work
                  </span>
                </div>
              </div>
              <div className="absolute -bottom-5 -left-5 w-24 h-24 rounded-xl border border-[color:var(--border-strong)] bg-[color:var(--surface)] backdrop-blur-xl flex flex-col items-center justify-center shadow-lg">
                <span className="font-display text-3xl text-[color:var(--accent)]">5+</span>
                <span className="label-mono">Years</span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="hidden lg:flex absolute right-8 bottom-8 flex-col items-center gap-2 text-[color:var(--text-muted)]"
        >
          <span className="label-mono">scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
          >
            <FiArrowDown />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
